import { forwardRef, useEffect, useRef, useState } from 'react';
import { Tourbook } from '@root/types';
import { useTranslation } from 'react-i18next';
import { Image, Transformation } from 'cloudinary-react';
import useEnv from '@root/shared/useEnv';
import cn from 'classnames';
import useAltText from '@root/shared/useAltText';
import Button from '@components/shared/V2Button';
import EditTourbookModal from '@components/shared/EditTourbookModal';
import { isInIframe } from '@root/shared/iframeUtils';
import useSize from '@react-hook/size';
import { IconButton, InvisibleMask, PopperTooltip, Notification } from '@components/shared';
import COLORS from '@root/shared/colorConstants';
import { colord } from 'colord';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMutation } from '@tanstack/react-query';
import { update } from '@root/shared/typedApi';
import routes from '@root/routes';
import useCurrentUser from '@root/shared/useCurrentUser';
import colorsInImage from '@root/shared/imageColorExtractor/imageColorExtractor';
import { ColorGroup } from '@components/shared/ColorPicker/ColorPickerColorGroup';
import { useBreakpoint } from '@root/shared/useBreakpoints';
import ClientInformation from './ClientInformation';
import ContactInformation from './ContactInformation';
import s from './Header.module.less';
import ColorPickerSidePanel, { ColorModule, useColorPickerSidePanel } from './ColorPickerSidePanel';
import BasicHeaderWithRepositioning from './BasicHeaderWithRepositioning';

type Props = {
  tourbook: Tourbook;
  readOnly: boolean;
  onEditClick: () => void;
  onCloseModal: () => void;
  isModalOpen: boolean;
  hidden: boolean;
};
const Header = forwardRef<HTMLDivElement | null, Props>(
  ({ tourbook, readOnly, onEditClick, onCloseModal, isModalOpen, hidden }: Props, ref) => {
    const { t } = useTranslation('tourbook');
    const { currentUser } = useCurrentUser();
    const flags = useFlags();

    const containsDescriptionBodyElements = tbook =>
      tbook.name && (tbook.description || tbook.clientName || tbook.clientLogoImage);

    const updateTourbookColorsMutation = useMutation(async (colors: { [key: string]: string }) =>
      update(routes.api.tourbook(tourbook.id), { colors }),
    );

    const [isCustomizingColors, setIsCustomizingColors] = useState(false);

    const clientLogo = tourbook?.clientLogoImage;
    const [clientLogoColors, setClientLogoColors] = useState<string[]>([]);

    const companyLogo = currentUser?.tourbookCompanyImage;
    const [companyLogoColors, setCompanyLogoColors] = useState<string[]>([]);

    useEffect(() => {
      if (clientLogo?.path) {
        colorsInImage({ imgSource: clientLogo.path }).then(setClientLogoColors);
      }

      if (companyLogo?.path) {
        colorsInImage({ imgSource: companyLogo.path }).then(setCompanyLogoColors);
      }
    }, [clientLogo, companyLogo]);

    const colorGroups: ColorGroup[] = [];

    if (clientLogo?.path)
      colorGroups.push({
        title: t('header.clientLogoColors'),
        colors: clientLogoColors,
        imgPath: clientLogo?.smallPath,
        altText: clientLogo?.description,
      });

    if (companyLogo?.path)
      colorGroups.push({
        title: t('header.companyLogoColors'),
        colors: companyLogoColors,
        imgPath: companyLogo?.smallPath,
        altText: companyLogo?.description,
      });

    const { colorModules, colorPickerSidePanelProps } = useColorPickerSidePanel({
      isOpen: isCustomizingColors,
      onClose: () => setIsCustomizingColors(false),
      onSave: newColorModules => {
        updateTourbookColorsMutation.mutate({
          header_color_1: newColorModules[0].color,
          header_color_2: newColorModules[1].color,
        });
        setIsCustomizingColors(false);
        Notification.info({
          title: t('colorPickerSidePanel.savedSuccessfully'),
        });
      },
      colorGroups,
      initialColorModules: [
        {
          title: t('colorPickerSidePanel.titleDescriptionBackground'),
          color: tourbook.colors?.headerColor1,
          defaultColor: COLORS.white,
        },
        {
          title: t('colorPickerSidePanel.contactInfoBackground'),
          color: tourbook.colors?.headerColor2,
          defaultColor: COLORS.indigo120,
        },
      ],
    });

    const renderBasicHeader = ({ isPreview = false }: { isPreview?: boolean } = {}) =>
      flags['demand.repositionable-tourbook-header'] ? (
        <BasicHeaderWithRepositioning
          tourbook={tourbook}
          readOnly={readOnly}
          hidden={hidden}
          isCustomizingColors={isCustomizingColors}
          containsDescriptionBodyElements={containsDescriptionBodyElements(tourbook)}
          colorModules={colorModules}
          setIsCustomizingColors={setIsCustomizingColors}
          isPreview={isPreview}
        />
      ) : (
        <BasicHeaderDeprecated
          tourbook={tourbook}
          readOnly={readOnly}
          onEditClick={onEditClick}
          hidden={hidden}
          isCustomizingColors={isCustomizingColors}
          containsDescriptionBodyElements={containsDescriptionBodyElements(tourbook)}
          colorModules={colorModules}
          setIsCustomizingColors={setIsCustomizingColors}
          isPreview={isPreview}
        />
      );

    return (
      <>
        <div
          id="tourbook-header"
          className={cn('phone:m-0 tablet:m-0', isInIframe() && '!mt-3')}
          ref={ref}
        >
          {renderBasicHeader()}
          <EditTourbookModal
            tourbook={tourbook}
            isModalOpen={isModalOpen}
            onCloseModal={onCloseModal}
          />
        </div>
        <ColorPickerSidePanel
          {...colorPickerSidePanelProps}
          previewElement={renderBasicHeader({ isPreview: true })}
        />
        {isCustomizingColors && isInIframe() && (
          <InvisibleMask className="!bottom-[calc(100%_-_64px)] !z-[53] cursor-default !bg-black-100/50" />
        )}
      </>
    );
  },
);

type BasicHeaderDeprecatedProps = {
  tourbook: Tourbook;
  readOnly: boolean;
  onEditClick: () => void;
  hidden: boolean;
  isCustomizingColors: boolean;
  containsDescriptionBodyElements: boolean;
  colorModules: ColorModule[];
  setIsCustomizingColors: (boolean) => void;
  isPreview: boolean;
};

const BasicHeaderDeprecated = ({
  tourbook,
  readOnly,
  onEditClick,
  hidden,
  isCustomizingColors,
  containsDescriptionBodyElements,
  colorModules,
  setIsCustomizingColors,
  isPreview,
}: BasicHeaderDeprecatedProps) => {
  const { getAltTextForTourbookHeader } = useAltText();
  const { t } = useTranslation('tourbook');
  const { cloudinaryCdn, cloudinaryCloud } = useEnv();
  const { isMobile } = useBreakpoint();

  const descriptionSection = colorModules[0];
  const contactSection = colorModules[1];

  const headerRef = useRef(null);
  const contactSectionRef = useRef(null);
  const headerHeight = useSize(headerRef)[1];
  const contactSectionHeight = useSize(contactSectionRef)[1];

  return (
    <header
      className={cn(
        'relative flex h-auto border-y border-solid border-general-neutral-secondary bg-background-primary shadow-[0px_1px_8px_rgba(115,_115,_115,_0.25)] md:min-h-[505px] md:border-x mobile:flex-col',
        hidden && '!hidden',
        isCustomizingColors && 'z-[51] md:!border-[#5E5F61]',
      )}
      ref={headerRef}
    >
      <div className="flex flex-col desktop:w-[53%] tablet:w-[60%]">
        <div
          data-testid={cn(isPreview ? 'previewDescriptionSection' : 'descriptionSection')}
          className={cn(
            'relative flex-grow border-2 border-solid transition-[color] duration-[.5s] mobile:flex mobile:min-h-[104px] mobile:items-center',
          )}
          style={{
            backgroundColor: descriptionSection.color,
            borderColor: descriptionSection.isFocused ? COLORS.egg100 : descriptionSection.color,
            color: colord(descriptionSection.color).isDark() ? 'white' : 'black',
          }}
        >
          <div
            className={cn(
              'flex h-full flex-col justify-center p-3 pr-4 mobile:!p-2',
              containsDescriptionBodyElements && 'justify-around',
            )}
          >
            <div className="flex flex-col gap-1">
              <h4 className="text-inherit font-headline mobile:font-title">{tourbook.name}</h4>
              {tourbook.description && (
                <span className="whitespace-pre-wrap font-subtitle-de-emphasis">
                  {tourbook.description}
                </span>
              )}
            </div>
            <ClientInformation name={tourbook.clientName} image={tourbook.clientLogoImage} />
          </div>
          {descriptionSection.isFocused && headerHeight && !isMobile ? (
            <div
              className="absolute inset-0 right-[-1px] bg-egg-100"
              style={{
                clipPath: `polygon(${
                  (1 - contactSectionHeight / headerHeight) * 63
                }px 0, 100% 0%, 100% 100%, 0 100%)`,
                left: `calc(100% - ${64 * (1 - contactSectionHeight / headerHeight)}px)`,
              }}
            />
          ) : null}
        </div>
        <div
          data-testid={cn(isPreview ? 'previewContactSection' : 'contactSection')}
          ref={contactSectionRef}
          className={cn('relative border-2 border-solid transition-[color] duration-[.5s]')}
          style={{
            backgroundColor: contactSection.color,
            borderColor: contactSection.isFocused ? COLORS.egg100 : contactSection.color,
            color: colord(contactSection.color).isDark() ? 'white' : 'black',
          }}
        >
          {tourbook && (
            <div className="flex p-3 mobile:p-2">
              <ContactInformation
                user={tourbook.owner}
                readOnly={readOnly || isInIframe()}
                darkMode={colord(contactSection.color).isDark()}
              />
            </div>
          )}
          {contactSection.isFocused && headerHeight && !isMobile ? (
            <div
              className="absolute inset-0 left-[calc(100%-64px)] bg-egg-100"
              style={{
                clipPath: `polygon(${
                  (contactSectionHeight / headerHeight) * 62
                }px 0, 100% 0%, 100% 100%, 0 100%)`,
              }}
            />
          ) : null}
          {!isCustomizingColors && !readOnly && !isPreview && (
            <PopperTooltip
              trigger="hover"
              triggerElementClassName="absolute bottom-2 right-10 mobile:right-2"
              toolTipPlacement="top"
              triggerElement={
                <IconButton
                  icon="paintbrush"
                  className="!h-4 !w-4 !border !border-solid !border-black-035 !bg-white !text-[14px] text-black-100 hover:!bg-black-005"
                  onClick={() => setIsCustomizingColors(true)}
                  data-testid="customizeColorsButton"
                />
              }
              popperElementClassName="bg-black-100 font-body-medium text-white"
              popperElement={t('header.customizeColors')}
            />
          )}
        </div>
      </div>
      <div
        className={cn(
          'md:absolute md:inset-0 desktop:left-[calc(53%-64px)] tablet:left-[calc(60%-64px)] mobile:order-[-1] mobile:flex',
          s.headerImageContainer,
          isCustomizingColors && !isPreview && 'brightness-50',
        )}
      >
        {tourbook.headerImage ? (
          <Image
            className={s.headerImage}
            cloudName={cloudinaryCloud}
            privateCdn={cloudinaryCdn}
            publicId={tourbook.headerImage.cloudinaryId}
            alt={getAltTextForTourbookHeader(tourbook.name)}
          >
            <Transformation
              crop="fill"
              fetchFormat="jpg"
              quality="100"
              width="1500"
              height="1500"
              gravity="auto"
            />
          </Image>
        ) : (
          <>
            <div className={s.uploadHeaderImageContainer}>
              {!readOnly && (
                <Button type="quarternary" onClick={onEditClick} className={s.uploadHeaderImage}>
                  {t('header.uploadHeaderImage')}
                </Button>
              )}
            </div>
            <img
              className={s.headerImagePlaceholder}
              src="/images/tourbook-header-default.jpg"
              alt="Tourbook Header"
            />
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
